/**
 * MenuBuilderPage — the core editor of the nutrition module (Phase 3).
 *
 * A menu is a tree: blocks (בלוקים) → sub-blocks (תת-בלוקים) → ingredients
 * (מזונות), where an ingredient may carry `connectedIngredients` — substitutes
 * the client may swap in, which the calculator AVERAGES with their parent.
 *
 * Everything numeric comes from src/utils/nutritionCalc; nothing is recomputed
 * here. `totals` on the document is display-only — the server recomputes it on
 * save.
 *
 * Route: /dashboard/patients/:patientId/menu/:menuId?
 * A missing :menuId means "new menu": we start from one empty block with one
 * empty sub-block and, on the first successful save, replace the URL with the
 * created menu's id so a refresh cannot create a second menu.
 *
 * FoodPickerModal / TemplatesSidebar / CoachNotesPanel are owned elsewhere and
 * consumed here against their fixed contracts.
 *
 * Error codes: CK-10100 (create), CK-10101 (update), CK-10102 (set active),
 *              CK-10103 (duplicate), CK-10104 (delete), CK-10105 (save
 *              template), CK-10106 (load menu), CK-10107 (load client),
 *              CK-10108 (add coach note), CK-10109 (delete coach note).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Plus, X } from "@phosphor-icons/react";

import {
  selectClinicId,
  selectIsNutritionClinic,
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  useCreateMenuMutation,
  useCreateMenuTemplateMutation,
  useDeleteMenuMutation,
  useDuplicateMenuMutation,
  useGetMenuQuery,
  useSetActiveMenuMutation,
  useUpdateMenuMutation,
} from "../../../store/services/menusApi";
import { useGetPatientQuery } from "../../../store/services/patientsApi";
import {
  MENU_TEMPLATE_KINDS,
  generateMenuPdf,
  buildMenuPdfPayload,
} from "../../../firebase";
import {
  GRAM_UNIT,
  calcBlockNutrition,
  calcMenuNutrition,
  calcSubBlockNutrition,
} from "../../../utils/nutritionCalc";
import { useRegisterUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import EmptyDraw from "../../../components/ui/EmptyDraw";

import MenuHeader from "../../../components/nutrition/menu/MenuHeader";
import MenuStatsBar from "../../../components/nutrition/menu/MenuStatsBar";
import BlockCard from "../../../components/nutrition/menu/BlockCard";
import FoodPickerModal from "../../../components/nutrition/menu/FoodPickerModal";
import TemplatesSidebar from "../../../components/nutrition/menu/TemplatesSidebar";
import CoachNotesPanel from "../../../components/nutrition/menu/CoachNotesPanel";
import MenuBuilderIntroModal from "../../../components/nutrition/menu/MenuBuilderIntroModal";
import "./MenuBuilderPage.css";
import ContentLoader from "../../../components/shared/ContentLoader";

// ============================================================================
// Pure tree helpers
// ============================================================================

let idCounter = 0;
/** Local-only id for a tree node. Server ids, when they exist, are preserved. */
const uid = (prefix) => {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
};

const emptySubBlock = () => ({
  subBlockId: uid("sub"),
  subBlockName: "",
  ingredients: [],
});

const emptyBlock = () => ({
  blockId: uid("blk"),
  blockName: "",
  comment: "",
  subBlocks: [emptySubBlock()],
});

/** Substitutes never nest, so a cloned substitute loses any nested list. */
const cloneIngredient = (ingredient) => ({
  ...ingredient,
  ingredientId: uid("ing"),
  connectedIngredients: (ingredient?.connectedIngredients || []).map((substitute) => ({
    ...substitute,
    ingredientId: uid("ing"),
    connectedIngredients: [],
  })),
});

const cloneSubBlock = (subBlock) => ({
  ...subBlock,
  subBlockId: uid("sub"),
  ingredients: (subBlock?.ingredients || []).map(cloneIngredient),
});

const cloneBlock = (block) => ({
  ...block,
  blockId: uid("blk"),
  subBlocks: (block?.subBlocks || []).map(cloneSubBlock),
});

/** Menus saved before this editor may lack ids; give every node a stable one. */
const hydrateBlocks = (blocks) =>
  (Array.isArray(blocks) ? blocks : []).map((block) => ({
    ...block,
    blockId: block.blockId || uid("blk"),
    comment: block.comment || "",
    subBlocks: (block.subBlocks || []).map((subBlock) => ({
      ...subBlock,
      subBlockId: subBlock.subBlockId || uid("sub"),
      ingredients: (subBlock.ingredients || []).map((ingredient) => ({
        ...ingredient,
        ingredientId: ingredient.ingredientId || uid("ing"),
        connectedIngredients: (ingredient.connectedIngredients || []).map((substitute) => ({
          ...substitute,
          ingredientId: substitute.ingredientId || uid("ing"),
          connectedIngredients: [],
        })),
      })),
    })),
  }));

/** doof's moveItem (CreateMenu.js:202), without the in-place splice. */
const moveItem = (list, fromIndex, direction) => {
  const toIndex = fromIndex + direction;
  if (toIndex < 0 || toIndex >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

const mapBlock = (blocks, blockIndex, fn) =>
  blocks.map((block, index) => (index === blockIndex ? fn(block) : block));

const mapSubBlock = (block, subBlockIndex, fn) => ({
  ...block,
  subBlocks: (block.subBlocks || []).map((subBlock, index) =>
    index === subBlockIndex ? fn(subBlock) : subBlock
  ),
});

const mapIngredient = (subBlock, ingredientIndex, fn) => ({
  ...subBlock,
  ingredients: (subBlock.ingredients || []).map((ingredient, index) =>
    index === ingredientIndex ? fn(ingredient) : ingredient
  ),
});

/** Amounts are edited as strings; the document stores numbers. */
const normalizeBlocks = (blocks) =>
  blocks.map((block) => ({
    ...block,
    subBlocks: (block.subBlocks || []).map((subBlock) => ({
      ...subBlock,
      ingredients: (subBlock.ingredients || []).map((ingredient) => ({
        ...ingredient,
        amount: Number(ingredient.amount) || 0,
        connectedIngredients: (ingredient.connectedIngredients || []).map((substitute) => ({
          ...substitute,
          amount: Number(substitute.amount) || 0,
        })),
      })),
    })),
  }));

/**
 * The validation cascade (ingredient → sub-block → block → menu), ported from
 * CreateMenu.js:907-1137 but collected instead of short-circuited, so the coach
 * sees every offending card at once rather than one toast per attempt.
 */
const validateMenuTree = (menuName, blocks) => {
  const errors = {};
  const order = [];
  const push = (key, message) => {
    if (errors[key]) return;
    errors[key] = message;
    order.push(key);
  };

  if (!menuName || !menuName.trim()) push("menu", "יש להזין שם לתפריט");

  // Block / sub-block names are NOT required: an unnamed one renders on the
  // client page with no heading (never a placeholder), and the card shows an
  // inline hint saying so. Only amounts are validated.
  blocks.forEach((block) => {
    (block.subBlocks || []).forEach((subBlock) => {
      (subBlock.ingredients || []).forEach((ingredient) => {
        if (!Number(ingredient.amount)) {
          push(
            `ing:${ingredient.ingredientId}`,
            `יש להזין כמות עבור "${ingredient.foodName}"`
          );
        }
        (ingredient.connectedIngredients || []).forEach((substitute, substituteIndex) => {
          if (!Number(substitute.amount)) {
            push(
              `ing:${substitute.ingredientId}`,
              `יש להזין כמות עבור חלופה ${substituteIndex + 2} — "${substitute.foodName}"`
            );
          }
        });
      });
    });
  });

  return { errors, order };
};

/** A template payload is refused when it is empty or holds an amount-less food. */
const templatePayloadError = (kind, payload) => {
  const ingredients =
    kind === MENU_TEMPLATE_KINDS.FOOD
      ? [payload]
      : kind === MENU_TEMPLATE_KINDS.SUB_BLOCK
      ? payload.ingredients || []
      : (payload.subBlocks || []).flatMap((subBlock) => subBlock.ingredients || []);

  if (ingredients.length === 0) return "לא ניתן לשמור תבנית ללא מזונות";

  const missing = ingredients.some(
    (ingredient) =>
      !Number(ingredient.amount) ||
      (ingredient.connectedIngredients || []).some((substitute) => !Number(substitute.amount))
  );
  if (missing) return "יש להזין כמות לכל המזונות לפני שמירת התבנית";
  return null;
};

/** Which tree level a template of this kind may be dropped into. */
const KIND_TO_LEVEL = {
  [MENU_TEMPLATE_KINDS.BLOCK]: "block",
  [MENU_TEMPLATE_KINDS.SUB_BLOCK]: "subBlock",
  [MENU_TEMPLATE_KINDS.FOOD]: "ingredient",
};

const PLACEMENT_HINT = {
  block: "בחר/י היכן להוסיף את הבלוק",
  subBlock: "בחר/י היכן להוסיף את התת-בלוק",
  ingredient: "בחר/י היכן להוסיף את המזון",
};

// ============================================================================
// Page
// ============================================================================

const MenuBuilderPage = () => {
  const { patientId, menuId } = useParams();
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);
  const { showSuccess, showError } = useToast();

  const pageRef = useRef(null);
  const flashTimerRef = useRef(null);

  // ─── Server data ───
  const {
    data: menu,
    isLoading: isMenuLoading,
    error: menuError,
  } = useGetMenuQuery(
    { clinicId, patientId, menuId },
    { skip: !clinicId || !patientId || !menuId }
  );

  const {
    data: patient,
    error: patientError,
    refetch: refetchPatient,
  } = useGetPatientQuery({ clinicId, patientId }, { skip: !clinicId || !patientId });

  const [createMenu, { isLoading: isCreating }] = useCreateMenuMutation();
  const [updateMenu, { isLoading: isUpdating }] = useUpdateMenuMutation();
  const [setActiveMenu, { isLoading: isActivating }] = useSetActiveMenuMutation();
  const [duplicateMenu, { isLoading: isDuplicating }] = useDuplicateMenuMutation();
  const [deleteMenu, { isLoading: isDeleting }] = useDeleteMenuMutation();
  const [createMenuTemplate, { isLoading: isSavingTemplate }] =
    useCreateMenuTemplateMutation();

  // ─── Editor state ───
  const [menuName, setMenuName] = useState("");
  const [comment, setComment] = useState("");
  const [isAveragesMenu, setIsAveragesMenu] = useState(false);
  const [blocks, setBlocks] = useState(() => [emptyBlock()]);
  const [coachNotes, setCoachNotes] = useState([]);

  const [activeOptions, setActiveOptions] = useState({}); // ingredientId → option index
  const [expandedOptions, setExpandedOptions] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [focusIngredientId, setFocusIngredientId] = useState(null);

  const [errors, setErrors] = useState({});
  const [flashKey, setFlashKey] = useState(null);

  const [picker, setPicker] = useState(null); // { mode, blockIndex, subBlockIndex, ingredientIndex }
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(null); // { kind, payload, totals }
  const [templateName, setTemplateName] = useState("");

  const [confirmActivate, setConfirmActivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [baseline, setBaseline] = useState(null);
  const hydratedRef = useRef(null);

  const isNew = !menuId;

  // ─── Hydration ───
  useEffect(() => {
    if (!menuId) {
      if (hydratedRef.current === "new") return;
      hydratedRef.current = "new";
      const fresh = [emptyBlock()];
      setMenuName("");
      setComment("");
      setIsAveragesMenu(false);
      setBlocks(fresh);
      setCoachNotes([]);
      setBaseline(JSON.stringify({ menuName: "", comment: "", isAveragesMenu: false, blocks: fresh }));
      return;
    }

    if (!menu || hydratedRef.current === menuId) return;
    hydratedRef.current = menuId;

    const nextBlocks = hydrateBlocks(menu.blocks);
    const safeBlocks = nextBlocks.length > 0 ? nextBlocks : [emptyBlock()];
    setMenuName(menu.menuName || "");
    setComment(menu.comment || "");
    setIsAveragesMenu(!!menu.isAveragesMenu);
    setBlocks(safeBlocks);
    setCoachNotes(menu.coachNotes || []);
    setBaseline(
      JSON.stringify({
        menuName: menu.menuName || "",
        comment: menu.comment || "",
        isAveragesMenu: !!menu.isAveragesMenu,
        blocks: safeBlocks,
      })
    );
  }, [menuId, menu]);

  useEffect(() => {
    if (!menuError) return;
    console.error("[CK-10106] Error loading menu:", {
      errorCode: "CK-10106",
      errorMessage: menuError?.message || "unknown",
      clinicId,
      patientId,
      menuId,
      timestamp: new Date().toISOString(),
    });
  }, [menuError, clinicId, patientId, menuId]);

  useEffect(() => {
    if (!patientError) return;
    console.error("[CK-10107] Error loading the menu's client:", {
      errorCode: "CK-10107",
      errorMessage: patientError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [patientError, clinicId, patientId]);

  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  // ─── Derived ───
  const snapshot = useMemo(
    () => JSON.stringify({ menuName, comment, isAveragesMenu, blocks }),
    [menuName, comment, isAveragesMenu, blocks]
  );
  const isDirty = baseline !== null && snapshot !== baseline;
  useRegisterUnsavedChanges(isDirty);

  const totals = useMemo(
    () => calcMenuNutrition(blocks, isAveragesMenu),
    [blocks, isAveragesMenu]
  );

  const goals = patient?.nutrition?.goals || null;
  const isActiveMenu = !!menuId && patient?.nutrition?.activeMenuId === menuId;
  const patientName = patient
    ? [patient.firstName, patient.lastName].filter(Boolean).join(" ")
    : "";

  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const isSaving = isCreating || isUpdating;
  const placementLevel = selectedTemplate ? KIND_TO_LEVEL[selectedTemplate.kind] : null;

  // ─── Flash + scroll for validation ───
  const flashCard = useCallback((key) => {
    setFlashKey(key);
    const node = pageRef.current?.querySelector(`[data-nmb-key="${key}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashKey(null), 900);
  }, []);

  // ─── Block mutators ───
  const handleBlockChange = useCallback((blockIndex, patch) => {
    setBlocks((prev) => mapBlock(prev, blockIndex, (block) => ({ ...block, ...patch })));
  }, []);

  const handleMoveBlock = useCallback(
    (blockIndex, direction) => {
      const key = `block:${blocks[blockIndex]?.blockId}`;
      setBlocks((prev) => moveItem(prev, blockIndex, direction));
      flashCard(key);
    },
    [blocks, flashCard]
  );

  const handleAddBlock = useCallback(() => {
    setBlocks((prev) => [...prev, emptyBlock()]);
  }, []);

  const handleDeleteBlock = useCallback((blockIndex) => {
    setBlocks((prev) => prev.filter((_, index) => index !== blockIndex));
  }, []);

  const handleToggleComment = useCallback((blockId) => {
    setOpenComments((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
  }, []);

  // ─── Sub-block mutators ───
  const handleSubBlockChange = useCallback((blockIndex, subBlockIndex, patch) => {
    setBlocks((prev) =>
      mapBlock(prev, blockIndex, (block) =>
        mapSubBlock(block, subBlockIndex, (subBlock) => ({ ...subBlock, ...patch }))
      )
    );
  }, []);

  const handleMoveSubBlock = useCallback(
    (blockIndex, subBlockIndex, direction) => {
      const key = `sub:${blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.subBlockId}`;
      setBlocks((prev) =>
        mapBlock(prev, blockIndex, (block) => ({
          ...block,
          subBlocks: moveItem(block.subBlocks || [], subBlockIndex, direction),
        }))
      );
      flashCard(key);
    },
    [blocks, flashCard]
  );

  const handleAddSubBlock = useCallback((blockIndex) => {
    setBlocks((prev) =>
      mapBlock(prev, blockIndex, (block) => ({
        ...block,
        subBlocks: [...(block.subBlocks || []), emptySubBlock()],
      }))
    );
  }, []);

  const handleDeleteSubBlock = useCallback((blockIndex, subBlockIndex) => {
    setBlocks((prev) =>
      mapBlock(prev, blockIndex, (block) => ({
        ...block,
        subBlocks: (block.subBlocks || []).filter((_, index) => index !== subBlockIndex),
      }))
    );
  }, []);

  // ─── Ingredient mutators ───
  const handleMoveIngredient = useCallback(
    (blockIndex, subBlockIndex, ingredientIndex, direction) => {
      const key = `ing:${
        blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.ingredients?.[ingredientIndex]
          ?.ingredientId
      }`;
      setBlocks((prev) =>
        mapBlock(prev, blockIndex, (block) =>
          mapSubBlock(block, subBlockIndex, (subBlock) => ({
            ...subBlock,
            ingredients: moveItem(subBlock.ingredients || [], ingredientIndex, direction),
          }))
        )
      );
      flashCard(key);
    },
    [blocks, flashCard]
  );

  const handleOptionChange = useCallback(
    (blockIndex, subBlockIndex, ingredientIndex, optionIndex, patch) => {
      setBlocks((prev) =>
        mapBlock(prev, blockIndex, (block) =>
          mapSubBlock(block, subBlockIndex, (subBlock) =>
            mapIngredient(subBlock, ingredientIndex, (ingredient) => {
              if (optionIndex === 0) return { ...ingredient, ...patch };
              return {
                ...ingredient,
                connectedIngredients: (ingredient.connectedIngredients || []).map(
                  (substitute, index) =>
                    index === optionIndex - 1 ? { ...substitute, ...patch } : substitute
                ),
              };
            })
          )
        )
      );
    },
    []
  );

  /**
   * Delete, ported from CreateMenu.js:535-614:
   *   - no substitutes            → the ingredient is removed
   *   - main option is showing    → the FIRST substitute is PROMOTED to main
   *                                 and inherits the rest of the family
   *   - a substitute is showing   → only that substitute is removed
   */
  const handleDeleteIngredient = useCallback(
    (blockIndex, subBlockIndex, ingredientIndex) => {
      const ingredient =
        blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.ingredients?.[ingredientIndex];
      if (!ingredient) return;

      const substitutes = ingredient.connectedIngredients || [];
      const activeIndex = Math.min(
        activeOptions[ingredient.ingredientId] || 0,
        substitutes.length
      );

      setBlocks((prev) =>
        mapBlock(prev, blockIndex, (block) =>
          mapSubBlock(block, subBlockIndex, (subBlock) => {
            if (substitutes.length === 0) {
              return {
                ...subBlock,
                ingredients: (subBlock.ingredients || []).filter(
                  (_, index) => index !== ingredientIndex
                ),
              };
            }

            if (activeIndex === 0) {
              const [promoted, ...rest] = substitutes;
              return mapIngredient(subBlock, ingredientIndex, () => ({
                ...promoted,
                connectedIngredients: rest,
              }));
            }

            return mapIngredient(subBlock, ingredientIndex, (current) => ({
              ...current,
              connectedIngredients: (current.connectedIngredients || []).filter(
                (_, index) => index !== activeIndex - 1
              ),
            }));
          })
        )
      );

      setActiveOptions((prev) => ({ ...prev, [ingredient.ingredientId]: 0 }));
    },
    [blocks, activeOptions]
  );

  const handleDeleteOption = useCallback(
    (blockIndex, subBlockIndex, ingredientIndex, optionIndex) => {
      if (optionIndex === 0) {
        handleDeleteIngredient(blockIndex, subBlockIndex, ingredientIndex);
        return;
      }
      const ingredientId =
        blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.ingredients?.[ingredientIndex]
          ?.ingredientId;

      setBlocks((prev) =>
        mapBlock(prev, blockIndex, (block) =>
          mapSubBlock(block, subBlockIndex, (subBlock) =>
            mapIngredient(subBlock, ingredientIndex, (ingredient) => ({
              ...ingredient,
              connectedIngredients: (ingredient.connectedIngredients || []).filter(
                (_, index) => index !== optionIndex - 1
              ),
            }))
          )
        )
      );
      if (ingredientId) setActiveOptions((prev) => ({ ...prev, [ingredientId]: 0 }));
    },
    [blocks, handleDeleteIngredient]
  );

  const handleCycleOption = useCallback((ingredientId, total) => {
    setActiveOptions((prev) => ({
      ...prev,
      [ingredientId]: ((prev[ingredientId] || 0) + 1) % total,
    }));
  }, []);

  const handleSetActiveOption = useCallback((ingredientId, optionIndex) => {
    setActiveOptions((prev) => ({ ...prev, [ingredientId]: optionIndex }));
  }, []);

  const handleToggleOptions = useCallback((ingredientId) => {
    setExpandedOptions((prev) => ({ ...prev, [ingredientId]: !prev[ingredientId] }));
  }, []);

  // ─── Food picker ───
  const handleAddFood = useCallback((blockIndex, subBlockIndex) => {
    setPicker({ mode: "food", blockIndex, subBlockIndex });
  }, []);

  const handleAddSubstitute = useCallback((blockIndex, subBlockIndex, ingredientIndex) => {
    setPicker({ mode: "substitute", blockIndex, subBlockIndex, ingredientIndex });
  }, []);

  const handlePickFood = useCallback(
    (picked) => {
      if (!picker) return;
      const ingredient = {
        ...picked,
        ingredientId: picked.ingredientId || uid("ing"),
        amount: 0,
        selectedAmount: picked.selectedAmount || GRAM_UNIT,
        connectedIngredients: [],
      };

      const { mode, blockIndex, subBlockIndex, ingredientIndex } = picker;

      if (mode === "substitute") {
        const parent =
          blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.ingredients?.[ingredientIndex];
        const nextIndex = (parent?.connectedIngredients?.length || 0) + 1;

        setBlocks((prev) =>
          mapBlock(prev, blockIndex, (block) =>
            mapSubBlock(block, subBlockIndex, (subBlock) =>
              mapIngredient(subBlock, ingredientIndex, (current) => ({
                ...current,
                connectedIngredients: [
                  ...(current.connectedIngredients || []),
                  ingredient,
                ],
              }))
            )
          )
        );

        if (parent?.ingredientId) {
          setActiveOptions((prev) => ({ ...prev, [parent.ingredientId]: nextIndex }));
          setFocusIngredientId(parent.ingredientId);
        }
      } else {
        setBlocks((prev) =>
          mapBlock(prev, blockIndex, (block) =>
            mapSubBlock(block, subBlockIndex, (subBlock) => ({
              ...subBlock,
              ingredients: [...(subBlock.ingredients || []), ingredient],
            }))
          )
        );
        setFocusIngredientId(ingredient.ingredientId);
      }

      setPicker(null);
    },
    [picker, blocks]
  );

  // ─── Template placement (two-step: select in the sidebar, then place) ───
  const canDrop = useCallback((level) => placementLevel === level, [placementLevel]);

  const handleDropHere = useCallback(
    (target) => {
      if (!selectedTemplate) return;
      const payload = selectedTemplate.payload;

      if (target.level === "block") {
        setBlocks((prev) => {
          const next = [...prev];
          next.splice(target.index, 0, cloneBlock(payload));
          return next;
        });
      } else if (target.level === "subBlock") {
        setBlocks((prev) =>
          mapBlock(prev, target.blockIndex, (block) => {
            const next = [...(block.subBlocks || [])];
            next.splice(target.index, 0, cloneSubBlock(payload));
            return { ...block, subBlocks: next };
          })
        );
      } else {
        setBlocks((prev) =>
          mapBlock(prev, target.blockIndex, (block) =>
            mapSubBlock(block, target.subBlockIndex, (subBlock) => {
              const next = [...(subBlock.ingredients || [])];
              next.splice(target.index, 0, cloneIngredient(payload));
              return { ...subBlock, ingredients: next };
            })
          )
        );
      }

      setSelectedTemplate(null);
    },
    [selectedTemplate]
  );

  useEffect(() => {
    if (!selectedTemplate) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") setSelectedTemplate(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [selectedTemplate]);

  // ─── Save as template ───
  const openTemplateDraft = useCallback(
    (kind, payload, templateTotals) => {
      const payloadError = templatePayloadError(kind, payload);
      if (payloadError) {
        showError(payloadError);
        return;
      }
      setTemplateName(
        kind === MENU_TEMPLATE_KINDS.BLOCK
          ? payload.blockName || ""
          : kind === MENU_TEMPLATE_KINDS.SUB_BLOCK
          ? payload.subBlockName || ""
          : payload.foodName || ""
      );
      setTemplateDraft({ kind, payload, totals: templateTotals });
    },
    [showError]
  );

  const handleSaveBlockTemplate = useCallback(
    (blockIndex) => {
      const block = blocks[blockIndex];
      if (!block) return;
      openTemplateDraft(
        MENU_TEMPLATE_KINDS.BLOCK,
        cloneBlock(block),
        calcBlockNutrition(block, isAveragesMenu)
      );
    },
    [blocks, isAveragesMenu, openTemplateDraft]
  );

  const handleSaveSubBlockTemplate = useCallback(
    (blockIndex, subBlockIndex) => {
      const subBlock = blocks[blockIndex]?.subBlocks?.[subBlockIndex];
      if (!subBlock) return;
      openTemplateDraft(
        MENU_TEMPLATE_KINDS.SUB_BLOCK,
        cloneSubBlock(subBlock),
        calcSubBlockNutrition(subBlock)
      );
    },
    [blocks, openTemplateDraft]
  );

  const handleSaveIngredientTemplate = useCallback(
    (blockIndex, subBlockIndex, ingredientIndex) => {
      const ingredient =
        blocks[blockIndex]?.subBlocks?.[subBlockIndex]?.ingredients?.[ingredientIndex];
      if (!ingredient) return;
      openTemplateDraft(
        MENU_TEMPLATE_KINDS.FOOD,
        cloneIngredient(ingredient),
        calcSubBlockNutrition({ ingredients: [ingredient] })
      );
    },
    [blocks, openTemplateDraft]
  );

  const handleConfirmTemplate = useCallback(async () => {
    if (!templateDraft) return;
    const name = templateName.trim();
    if (!name) {
      showError("יש להזין שם לתבנית");
      return;
    }

    try {
      await createMenuTemplate({
        clinicId,
        templateData: {
          kind: templateDraft.kind,
          name,
          payload: templateDraft.payload,
          totals: templateDraft.totals,
        },
        therapistId,
        therapistName,
      }).unwrap();
      showSuccess("התבנית נשמרה ותופיע ברשימת התבניות");
      setTemplateDraft(null);
      setTemplateName("");
    } catch (error) {
      console.error("[CK-10105] Error saving a menu template:", {
        errorCode: "CK-10105",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        kind: templateDraft.kind,
        timestamp: new Date().toISOString(),
      });
      showError("שמירת התבנית נכשלה, נסו שוב");
    }
  }, [
    templateDraft,
    templateName,
    clinicId,
    patientId,
    therapistId,
    therapistName,
    createMenuTemplate,
    showSuccess,
    showError,
  ]);

  // ─── Save ───
  const handleSave = useCallback(async () => {
    const { errors: nextErrors, order } = validateMenuTree(menuName, blocks);
    setErrors(nextErrors);

    if (order.length > 0) {
      const firstKey = order[0];
      if (firstKey !== "menu") flashCard(firstKey);
      showError(
        order.length > 1
          ? `${nextErrors[firstKey]} (ועוד ${order.length - 1} תיקונים נדרשים)`
          : nextErrors[firstKey]
      );
      return;
    }

    const normalized = normalizeBlocks(blocks);
    const menuData = {
      menuName: menuName.trim(),
      comment,
      isAveragesMenu,
      blocks: normalized,
      coachNotes,
    };

    if (isNew) {
      try {
        const created = await createMenu({
          clinicId,
          patientId,
          menuData,
          therapistId,
          therapistName,
        }).unwrap();

        const createdId = created?.id || created?.menuId;
        setBlocks(normalized);
        setBaseline(
          JSON.stringify({ menuName: menuName.trim(), comment, isAveragesMenu, blocks: normalized })
        );
        showSuccess(
          created?.autoActivated
            ? "התפריט נשמר והוגדר אוטומטית כתפריט הפעיל"
            : "התפריט נשמר בהצלחה"
        );
        // A first menu auto-activates server-side — the ribbon reads the patient doc
        if (created?.autoActivated) refetchPatient();

        if (createdId) {
          hydratedRef.current = createdId;
          navigate(`/dashboard/patients/${patientId}/menu/${createdId}`, { replace: true });
        }
      } catch (error) {
        console.error("[CK-10100] Error creating a menu:", {
          errorCode: "CK-10100",
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          timestamp: new Date().toISOString(),
        });
        showError("שמירת התפריט נכשלה, נסו שוב");
      }
      return;
    }

    try {
      await updateMenu({
        clinicId,
        patientId,
        menuId,
        menuData,
        therapistId,
        therapistName,
      }).unwrap();
      setBlocks(normalized);
      setBaseline(
        JSON.stringify({ menuName: menuName.trim(), comment, isAveragesMenu, blocks: normalized })
      );
      showSuccess("התפריט נשמר בהצלחה");
    } catch (error) {
      console.error("[CK-10101] Error updating a menu:", {
        errorCode: "CK-10101",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        menuId,
        timestamp: new Date().toISOString(),
      });
      showError("שמירת התפריט נכשלה, נסו שוב");
    }
  }, [
    menuName,
    blocks,
    comment,
    isAveragesMenu,
    coachNotes,
    isNew,
    clinicId,
    patientId,
    menuId,
    therapistId,
    therapistName,
    createMenu,
    updateMenu,
    navigate,
    flashCard,
    refetchPatient,
    showSuccess,
    showError,
  ]);

  // ─── Coach notes (persisted with the menu document) ───
  const persistNotes = useCallback(
    async (nextNotes, errorCode) => {
      setCoachNotes(nextNotes);
      if (!menuId) return; // an unsaved menu keeps its notes locally until the first save
      try {
        await updateMenu({
          clinicId,
          patientId,
          menuId,
          menuData: {
            menuName: menuName.trim(),
            comment,
            isAveragesMenu,
            blocks: normalizeBlocks(blocks),
            coachNotes: nextNotes,
          },
          therapistId,
          therapistName,
        }).unwrap();
      } catch (error) {
        console.error(`[${errorCode}] Error saving a menu coach note:`, {
          errorCode,
          errorMessage: error?.message || error?.data?.message || "unknown",
          clinicId,
          patientId,
          menuId,
          timestamp: new Date().toISOString(),
        });
        showError("שמירת ההערה נכשלה, נסו שוב");
      }
    },
    [
      menuId,
      clinicId,
      patientId,
      menuName,
      comment,
      isAveragesMenu,
      blocks,
      therapistId,
      therapistName,
      updateMenu,
      showError,
    ]
  );

  const handleAddNote = useCallback(
    (content) =>
      persistNotes(
        [
          ...coachNotes,
          {
            id: uid("note"),
            authorId: therapistId,
            authorName: therapistName,
            content,
            createdAt: new Date().toISOString(),
          },
        ],
        "CK-10108"
      ),
    [coachNotes, therapistId, therapistName, persistNotes]
  );

  const handleDeleteNote = useCallback(
    (noteId) =>
      persistNotes(
        coachNotes.filter((note) => note.id !== noteId),
        "CK-10109"
      ),
    [coachNotes, persistNotes]
  );

  // ─── Header actions ───
  const handleSetActive = useCallback(async () => {
    try {
      await setActiveMenu({
        clinicId,
        patientId,
        menuId,
        therapistId,
        therapistName,
      }).unwrap();
      refetchPatient();
      showSuccess("התפריט הוגדר כתפריט הפעיל של הלקוח/ה");
    } catch (error) {
      console.error("[CK-10102] Error setting the active menu:", {
        errorCode: "CK-10102",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        menuId,
        timestamp: new Date().toISOString(),
      });
      showError("הגדרת התפריט הפעיל נכשלה, נסו שוב");
    }
  }, [
    clinicId,
    patientId,
    menuId,
    therapistId,
    therapistName,
    setActiveMenu,
    refetchPatient,
    showSuccess,
    showError,
  ]);

  // ─── PDF export ───
  // The payload is built by the shared builder in firebase.js so this page and
  // the menus list can never produce two differently-shaped PDFs.
  const handleExportPdf = useCallback(async () => {
    if (!menuId || isDirty) return;
    setIsExportingPdf(true);
    try {
      const result = await generateMenuPdf({
        clinicId,
        patientId,
        menuId,
        menu: buildMenuPdfPayload(
          { menuName, comment, isAveragesMenu, blocks },
          patientName
        ),
      });
      if (result?.pdfUrl) {
        window.open(result.pdfUrl, "_blank", "noopener,noreferrer");
        showSuccess("קובץ ה-PDF נוצר");
      }
    } catch (error) {
      console.error("[CK-10110] Error exporting a menu to PDF:", {
        errorCode: "CK-10110",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        menuId,
        timestamp: new Date().toISOString(),
      });
      showError("ייצוא ה-PDF נכשל, נסו שוב");
    } finally {
      setIsExportingPdf(false);
    }
  }, [
    clinicId,
    patientId,
    menuId,
    isDirty,
    menuName,
    comment,
    isAveragesMenu,
    blocks,
    patientName,
    showSuccess,
    showError,
  ]);

  const handleDuplicate = useCallback(async () => {
    try {
      const copy = await duplicateMenu({
        clinicId,
        patientId,
        menuId,
        therapistId,
        therapistName,
      }).unwrap();
      const copyId = copy?.id || copy?.menuId;
      showSuccess("התפריט שוכפל בהצלחה");
      if (copyId) {
        setBaseline(snapshot);
        navigate(`/dashboard/patients/${patientId}/menu/${copyId}`);
      }
    } catch (error) {
      console.error("[CK-10103] Error duplicating a menu:", {
        errorCode: "CK-10103",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        menuId,
        timestamp: new Date().toISOString(),
      });
      showError("שכפול התפריט נכשל, נסו שוב");
    }
  }, [
    clinicId,
    patientId,
    menuId,
    therapistId,
    therapistName,
    duplicateMenu,
    navigate,
    snapshot,
    showSuccess,
    showError,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteMenu({
        clinicId,
        patientId,
        menuId,
        reason: "נמחק על ידי המטפל/ת ממסך בניית התפריט",
        therapistId,
        therapistName,
      }).unwrap();
      setBaseline(snapshot);
      showSuccess("התפריט נמחק");
      navigate(`/dashboard/nutrition/${patientId}?tab=menus`);
    } catch (error) {
      console.error("[CK-10104] Error deleting a menu:", {
        errorCode: "CK-10104",
        errorMessage: error?.message || error?.data?.message || "unknown",
        clinicId,
        patientId,
        menuId,
        timestamp: new Date().toISOString(),
      });
      showError("מחיקת התפריט נכשלה, נסו שוב");
    }
  }, [
    clinicId,
    patientId,
    menuId,
    therapistId,
    therapistName,
    deleteMenu,
    navigate,
    snapshot,
    showSuccess,
    showError,
  ]);

  // ─── Context handed to the card tree ───
  const ctx = useMemo(
    () => ({
      isAveragesMenu,
      errors,
      flashKey,
      activeOptions,
      expandedOptions,
      openComments,
      focusIngredientId,
      canDrop,
      onDropHere: handleDropHere,
      onFocusHandled: () => setFocusIngredientId(null),
      onBlockChange: handleBlockChange,
      onMoveBlock: handleMoveBlock,
      onDeleteBlock: handleDeleteBlock,
      onToggleComment: handleToggleComment,
      onSaveBlockTemplate: handleSaveBlockTemplate,
      onAddSubBlock: handleAddSubBlock,
      onSubBlockChange: handleSubBlockChange,
      onMoveSubBlock: handleMoveSubBlock,
      onDeleteSubBlock: handleDeleteSubBlock,
      onSaveSubBlockTemplate: handleSaveSubBlockTemplate,
      onAddFood: handleAddFood,
      onAddSubstitute: handleAddSubstitute,
      onMoveIngredient: handleMoveIngredient,
      onOptionChange: handleOptionChange,
      onDeleteIngredient: handleDeleteIngredient,
      onDeleteOption: handleDeleteOption,
      onCycleOption: handleCycleOption,
      onSetActiveOption: handleSetActiveOption,
      onToggleOptions: handleToggleOptions,
      onSaveIngredientTemplate: handleSaveIngredientTemplate,
    }),
    [
      isAveragesMenu,
      errors,
      flashKey,
      activeOptions,
      expandedOptions,
      openComments,
      focusIngredientId,
      canDrop,
      handleDropHere,
      handleBlockChange,
      handleMoveBlock,
      handleDeleteBlock,
      handleToggleComment,
      handleSaveBlockTemplate,
      handleAddSubBlock,
      handleSubBlockChange,
      handleMoveSubBlock,
      handleDeleteSubBlock,
      handleSaveSubBlockTemplate,
      handleAddFood,
      handleAddSubstitute,
      handleMoveIngredient,
      handleOptionChange,
      handleDeleteIngredient,
      handleDeleteOption,
      handleCycleOption,
      handleSetActiveOption,
      handleToggleOptions,
      handleSaveIngredientTemplate,
    ]
  );

  // Defensive gate — routing also guards this page.
  if (!isNutritionClinic) return null;

  const showLoader = !!menuId && isMenuLoading && !menu;
  const showNotFound = !!menuId && !isMenuLoading && !menu;

  const blockDropZone = (position) =>
    canDrop("block") ? (
      <button
        type="button"
        className="nmb-dropzone block"
        onClick={() => handleDropHere({ level: "block", index: position })}
      >
        הוסף כאן
      </button>
    ) : null;

  return (
    <div className={`nmb-page ${selectedTemplate ? "placing" : ""}`} ref={pageRef}>
      <MenuHeader
        patientName={patientName}
        menuName={menuName}
        nameError={errors.menu}
        onMenuNameChange={setMenuName}
        comment={comment}
        onCommentChange={setComment}
        isAveragesMenu={isAveragesMenu}
        onToggleAverages={() => setIsAveragesMenu((value) => !value)}
        isActiveMenu={isActiveMenu}
        isNew={isNew}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onSetActive={() => setConfirmActivate(true)}
        onDuplicate={handleDuplicate}
        onExportPdf={handleExportPdf}
        isExportingPdf={isExportingPdf}
        onDelete={() => setConfirmDelete(true)}
        onBack={() => navigate(`/dashboard/nutrition/${patientId}?tab=menus`)}
      />

      {showLoader ? (
        <ContentLoader fill message="טוען את התפריט..." />
      ) : showNotFound ? (
        <div className="nmb-empty">
          <EmptyDraw variant="search" />
          <p>התפריט המבוקש לא נמצא</p>
          <button
            type="button"
            className="nmb-secondary-btn"
            onClick={() => navigate(`/dashboard/nutrition/${patientId}?tab=menus`)}
          >
            חזרה לתפריטים של הלקוח/ה
          </button>
        </div>
      ) : (
        <div className="nmb-layout">
          <aside className="nmb-sidebar">
            <TemplatesSidebar
              clinicId={clinicId}
              selectedTemplate={selectedTemplate}
              onSelectTemplate={setSelectedTemplate}
            />
          </aside>

          <div className="nmb-editor">
            {blocks.length === 0 && !canDrop("block") ? (
              <div className="nmb-empty">
                <EmptyDraw variant="template" />
                <p>התפריט עדיין ריק — מתחילים בהוספת בלוק ראשון (למשל ארוחת בוקר)</p>
                <button type="button" className="nmb-secondary-btn" onClick={handleAddBlock}>
                  <Plus size={16} weight="bold" />
                  <span>הוספת בלוק</span>
                </button>
              </div>
            ) : (
              <>
                {blockDropZone(0)}

                {blocks.map((block, index) => (
                  <div key={block.blockId}>
                    <BlockCard block={block} index={index} count={blocks.length} ctx={ctx} />
                    {blockDropZone(index + 1)}
                  </div>
                ))}

                <button type="button" className="nmb-add-block" onClick={handleAddBlock}>
                  <Plus size={16} weight="bold" />
                  <span>הוספת בלוק</span>
                </button>
              </>
            )}

            <div className="nmb-notes">
              <CoachNotesPanel
                notes={coachNotes}
                onAdd={handleAddNote}
                onDelete={handleDeleteNote}
                title="הערות מאמן/ת"
              />
            </div>
          </div>
        </div>
      )}

      {/* Two-step template placement */}
      {selectedTemplate && (
        <>
          <div
            className="nmb-scrim"
            role="presentation"
            onClick={() => setSelectedTemplate(null)}
          />
          <div className="nmb-placement-card" role="status">
            <span className="nmb-placement-title">
              {PLACEMENT_HINT[placementLevel] || "בחר/י היכן להוסיף"}
            </span>
            <span className="nmb-placement-name">{selectedTemplate.name}</span>
            <button
              type="button"
              className="nmb-secondary-btn"
              onClick={() => setSelectedTemplate(null)}
            >
              <X size={14} weight="bold" />
              <span>ביטול</span>
            </button>
          </div>
        </>
      )}

      {picker && (
        <FoodPickerModal
          mode={picker.mode}
          clinicId={clinicId}
          onClose={() => setPicker(null)}
          onSelect={handlePickFood}
        />
      )}

      <ConfirmDialog
        isOpen={!!templateDraft}
        onClose={() => {
          setTemplateDraft(null);
          setTemplateName("");
        }}
        onConfirm={handleConfirmTemplate}
        title="שמירת תבנית"
        message="התבנית תישמר לכל התפריטים במרפאה ותופיע ברשימת התבניות."
        confirmText="שמירה"
        cancelText="ביטול"
        type="info"
        icon="duplicate"
        confirmDisabled={isSavingTemplate || !templateName.trim()}
      >
        <label className="nmb-dialog-field">
          <span className="nmb-field-label">שם התבנית</span>
          <input
            type="text"
            className="nmb-dialog-input"
            value={templateName}
            onChange={(event) => setTemplateName(event.target.value)}
            placeholder="לדוגמה: ארוחת בוקר חלבונית"
            aria-label="שם התבנית"
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirmActivate}
        onClose={() => setConfirmActivate(false)}
        onConfirm={handleSetActive}
        title="הגדרת תפריט פעיל"
        message="ללקוח/ה יכול להיות תפריט פעיל אחד בלבד. התפריט הפעיל הקודם יפסיק להופיע."
        confirmText="הגדרה כתפריט פעיל"
        cancelText="ביטול"
        type="warning"
        confirmDisabled={isActivating}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="מחיקת תפריט"
        message="התפריט יוסר מרשימת התפריטים של הלקוח/ה. לא ניתן לשחזר תפריט שנמחק."
        confirmText="מחיקה"
        cancelText="ביטול"
        type="danger"
        icon="delete"
        confirmDisabled={isDeleting}
      />

      {/* ONE totals surface, pinned to the bottom — never a second one up top */}
      {!showLoader && !showNotFound && (
        <MenuStatsBar totals={totals} goals={goals} isAveragesMenu={isAveragesMenu} />
      )}

      {(isDuplicating || isActivating) && (
        <span className="nmb-sr-only" role="status">
          מעבד פעולה...
        </span>
      )}

      {/* First-visits explanation — counts views on the therapist doc, self-gates */}
      {!showLoader && !showNotFound && <MenuBuilderIntroModal />}
    </div>
  );
};

export default MenuBuilderPage;
